import { EventEmitter, Component, Input, OnInit, Output, inject } from '@angular/core';
import { UploadOutput, UploadInput, UploadFile, humanizeBytes, UploaderOptions, NgxUploaderModule } from 'ngx-uploader';
import { MediaService } from '../../media.service';
import { AwareHttpService } from '@appbolaget/aware-http';
import { Bucket } from '@appbolaget/aware-model';
import { Config } from '@services/config';
import { AwareAuthService } from '@appbolaget/aware-auth';
import { TranslateModule } from '@ngx-translate/core';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { NgClass, NgFor } from '@angular/common';
import { firstValueFrom, take } from 'rxjs';

@Component({
    selector: 'aw-file-uploader',
    templateUrl: 'file-uploader.component.html',
    styleUrls: ['./file-uploader.component.scss'],
    standalone: true,
    imports: [NgClass, MatIconModule, MatButtonModule, NgxUploaderModule, NgFor, MatProgressBarModule, TranslateModule],
})
export class FileUploaderComponent implements OnInit {
    options: UploaderOptions = {
        concurrency: 1,
    };

    formData: FormData;
    files: UploadFile[];
    uploadedFiles: UploadFile[] = [];
    uploadInput: EventEmitter<UploadInput>;
    humanizeBytes: Function;

    DRAG_OVER: boolean;

    @Input() bucket: Bucket;
    @Input() uploadParams: any;

    @Output() uploadFinished = new EventEmitter();

    private awareAuth = inject(AwareAuthService);
    constructor(
        public api: AwareHttpService,
        public config: Config,
        public mediaService: MediaService,
    ) {
        this.files = [];
        this.uploadInput = new EventEmitter<UploadInput>();
        this.humanizeBytes = humanizeBytes;
    }

    ngOnInit() {}

    async onUploadOutput(output: UploadOutput): Promise<void> {
        if (output.type === 'allAddedToQueue') {
            // when all files added in queue
            // uncomment this if you want to auto upload files when added

            const tempHeaders: any = {
                Token: this.awareAuth.token,
                Module: 'media',
            };

            if (this.mediaService.unit) {
                tempHeaders.Unit = this.mediaService.unit.uuid;
            }

            const currentHeaders = this.api.headers.keys().reduce((acc, curr) => {
                if (curr.toLowerCase() === 'token' || curr.toLowerCase() === 'module' || curr.toLowerCase() === 'unit') {
                    return acc;
                }

                acc[curr] = this.api.headers.get(curr);
                return acc;
            }, {});

            const headers = Object.assign({}, currentHeaders, tempHeaders);

            const data: any = { ...(this.uploadParams ? this.uploadParams : {}) };

            if (this.bucket.uuid) {
                data.bucket_id = this.bucket.uuid;
            }

            if (this.mediaService.activeLibraryMode === 'client') {
                data.private = true;

                if (this.mediaService.activeLibraryClient) {
                    data.client_id = this.mediaService.activeLibraryClient;
                }
            }

            delete headers['Content-Type'];

            const env = await firstValueFrom(this.api.apiEnv$.pipe(take(1)));

            const event: UploadInput = {
                type: 'uploadAll',
                url: `${env.url}/files`,
                method: 'POST',
                headers: headers,
                data: data,
            };

            this.uploadInput.emit(event);
        } else if (output.type === 'done') {
            this.uploadedFiles.push(output.file);

            if (this.uploadedFiles.length >= this.files.length) {
                this.uploadDidFinish();
            }
        } else if (output.type === 'addedToQueue' && typeof output.file !== 'undefined') {
            // add file to array when added
            this.files.push(output.file);
        } else if (output.type === 'uploading' && typeof output.file !== 'undefined') {
            // update current data in files array for uploading file
            const index = this.files.findIndex((file) => typeof output.file !== 'undefined' && file.id === output.file.id);
            this.files[index] = output.file;
        } else if (output.type === 'removed') {
            // remove file from array when removed
            this.files = this.files.filter((file: UploadFile) => file !== output.file);
        } else if (output.type === 'dragOver') {
            this.DRAG_OVER = true;
        } else if (output.type === 'dragOut') {
            this.DRAG_OVER = false;
        } else if (output.type === 'drop') {
            this.DRAG_OVER = false;
        }
    }

    humanFileSize(bytes, si: boolean = false) {
        const thresh = si ? 1000 : 1024;

        if (Math.abs(bytes) < thresh) {
            return bytes + ' B';
        }

        const units = si ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'] : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];

        let u = -1;

        do {
            bytes /= thresh;
            ++u;
        } while (Math.abs(bytes) >= thresh && u < units.length - 1);

        return bytes.toFixed(1) + ' ' + units[u];
    }

    cancelUpload(id: string): void {
        this.uploadInput.emit({ type: 'cancel', id: id });
    }

    removeFile(id: string): void {
        this.uploadInput.emit({ type: 'remove', id: id });
    }

    removeAllFiles(): void {
        this.uploadInput.emit({ type: 'removeAll' });
    }

    uploadDidFinish() {
        this.uploadFinished.emit(true);
        this.files = [];
        this.uploadedFiles = [];
    }
}
